import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, MapPin, Sparkles, ArrowRight, Calendar, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { TRAVEL_STORIES, TRAVEL_MOODS } from '@/data/discoveryData';
import { useCountry } from '@/contexts/CountryContext';
import { HorizontalCarousel } from '@/components/HorizontalCarousel';
import { directory } from '@/lib/directory';
import { listingUrl } from '@/lib/seo';

const API = process.env.REACT_APP_BACKEND_URL;
const niceCity = (s) => (s || '').replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// ============= AI Trip Planner Banner =============
export const PlanMyTripBanner = () => (
  <section className="relative py-12 overflow-hidden bg-gradient-to-br from-[#0F1929] via-[#102032] to-[#1A2A40] text-white" data-testid="plan-trip-banner">
    <div className="absolute top-1/2 -translate-y-1/2 -left-32 w-[420px] h-[420px] rounded-full bg-[#00AA6C]/25 blur-[120px] pointer-events-none" />
    <div className="absolute bottom-0 right-0 w-[300px] h-[300px] rounded-full bg-[#D32323]/15 blur-[100px] pointer-events-none" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1.3fr_1fr] gap-8 items-center">
      <div>
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-xs mb-4">
          <Sparkles className="w-3 h-3 text-[#00AA6C]" /> NEW · AI Trip Architect
        </div>
        <h2 className="font-heading text-3xl sm:text-4xl font-bold leading-tight">
          Tell us your dream trip.<br />
          <span className="text-[#00AA6C]">We&apos;ll write the itinerary for you.</span>
        </h2>
        <p className="text-white/75 mt-3 max-w-xl">
          Pick your dates, vibe and budget — our AI builds a full day-by-day plan with real hotels, restaurants and hidden gems across the Balkans.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Button variant="green" size="lg" asChild>
            <Link to="/plan-trip" data-testid="banner-plan-trip-cta"><Sparkles className="w-4 h-4" /> Plan my trip</Link>
          </Button>
          <Button variant="glass" size="lg" asChild>
            <Link to="/ai">Chat with concierge</Link>
          </Button>
        </div>
      </div>
      <div className="hidden lg:grid grid-cols-3 gap-2">
        {TRAVEL_MOODS.slice(0, 9).map((m) => (
          <Link
            key={m.id}
            to={`/search?mood=${m.id}`}
            className="aspect-square rounded-xl flex flex-col items-center justify-center text-center p-2 transition-transform hover:scale-105 border border-white/10"
            style={{ background: `${m.color}1f` }}
          >
            <span className="text-2xl mb-1">{m.emoji}</span>
            <span className="text-[10px] text-white/85 font-medium leading-tight">{m.label}</span>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

// ============= Trending Now =============
// Moved to standalone component — see /app/frontend/src/components/TrendingNow.jsx
export { TrendingNow } from '@/components/TrendingNow';

// ============= Hidden Gems (API-driven: high rating, still under the radar) =============
export const HiddenGems = () => {
  const { country } = useCountry();
  const [items, setItems] = useState(null);

  useEffect(() => {
    let alive = true;
    directory.listings({ country: country.code, sort: 'rating', limit: 120 })
      .then((d) => {
        if (!alive) return;
        const gems = (d.items || [])
          .filter((b) => (b.rating || 0) >= 4.5 && (b.reviews_count || 0) <= 400 && !b.premium && !b.featured && b.image)
          .slice(0, 10);
        setItems(gems);
      })
      .catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  }, [country.code]);

  if (!items || items.length === 0) return null;

  return (
    <section className="py-16 bg-gradient-to-br from-sky-50 to-white" data-testid="hidden-gems">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-6 gap-4">
          <div>
            <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-sky-600 mb-1">
              🗝️ Hidden Gems
            </div>
            <h2 className="font-heading text-3xl font-bold text-gray-900">Places only locals know</h2>
            <p className="text-gray-600 mt-1 text-sm">Top-rated spots the crowds haven&apos;t found yet</p>
          </div>
          <Button variant="outline" size="sm" asChild className="shrink-0">
            <Link to="/search?mood=hidden-gems">All gems <ArrowRight className="w-3 h-3" /></Link>
          </Button>
        </div>
        <HorizontalCarousel testid="gems-carousel">
          {items.map((b) => (
            <Link key={b.id} to={listingUrl(b.type, b.slug)} className="group relative rounded-2xl overflow-hidden block w-72 sm:w-80 h-80 shadow-md hover:shadow-2xl transition-all" data-testid={`gem-${b.slug || b.id}`}>
              <img src={b.image} alt={b.title || b.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
              <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-white/95 text-sky-700 text-xs font-bold backdrop-blur shadow">
                🗝️ Hidden gem
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                <h3 className="font-heading font-bold text-xl leading-tight">{b.title || b.name}</h3>
                <p className="text-sm text-white/85 mt-1 line-clamp-2">{b.address || niceCity(b.city)}</p>
                <span className="inline-flex items-center gap-1 mt-3 text-xs font-semibold text-[#00AA6C] bg-white px-2.5 py-1 rounded-full">
                  Discover <ArrowRight className="w-3 h-3" />
                </span>
              </div>
            </Link>
          ))}
        </HorizontalCarousel>
      </div>
    </section>
  );
};

// ============= Tonight in City (real events from the API) =============
export const TonightInCity = () => {
  const { country } = useCountry();
  const [items, setItems] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/events?status=upcoming&limit=12`)
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => setItems(d.items || []))
      .catch(() => setItems([]));
  }, []);

  if (!items || items.length === 0) return null;

  const fmtDate = (iso) => {
    try {
      const d = new Date(iso);
      return `${d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })} · ${d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;
    } catch { return ''; }
  };

  return (
    <section className="py-16 bg-white" data-testid="tonight-in-city">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-6 gap-4">
          <div>
            <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-amber-600 mb-1">
              <Clock className="w-3.5 h-3.5" /> Happening now
            </div>
            <h2 className="font-heading text-3xl font-bold text-gray-900">Upcoming events in {country.name}</h2>
            <p className="text-gray-600 mt-1 text-sm">Live music, beach parties, sunset cruises</p>
          </div>
          <Button variant="outline" size="sm" asChild className="shrink-0">
            <Link to="/events">All events <ArrowRight className="w-3 h-3" /></Link>
          </Button>
        </div>
        <HorizontalCarousel testid="events-carousel">
          {items.map((e) => (
            <Link key={e.id} to="/events" className="group block w-72 sm:w-80 bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all" data-testid={`event-${e.id}`}>
              {e.image && (
                <div className="relative h-32 overflow-hidden">
                  <img src={e.image} alt={e.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                </div>
              )}
              <div className="p-4">
                <Badge className="bg-amber-100 text-amber-800 border-0 text-[10px] uppercase">{e.category}</Badge>
                <h3 className="font-heading font-bold mt-2 group-hover:text-[#00AA6C] transition-colors line-clamp-2">{e.title}</h3>
                <div className="text-xs text-gray-500 mt-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> {fmtDate(e.start_at)}</div>
                <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1 capitalize"><MapPin className="w-3 h-3" /> {e.venue ? `${e.venue} · ` : ''}{niceCity(e.city)}</div>
              </div>
            </Link>
          ))}
        </HorizontalCarousel>
      </div>
    </section>
  );
};

// ============= Travel Stories =============
export const TravelStoriesSection = () => {
  const { country } = useCountry();
  const stories = TRAVEL_STORIES.filter(s => s.country === country.code).slice(0, 4);
  if (stories.length === 0) return null;
  const [hero, ...rest] = stories;

  return (
    <section className="py-16 bg-gray-50" data-testid="travel-stories-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-6 gap-4">
          <div>
            <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#00AA6C] mb-1">
              <TrendingUp className="w-3.5 h-3.5" /> Travel Stories
            </div>
            <h2 className="font-heading text-3xl font-bold text-gray-900">Long reads from the road</h2>
            <p className="text-gray-600 mt-1 text-sm">Editor-crafted itineraries with maps and recommendations</p>
          </div>
          <Button variant="outline" size="sm" asChild className="shrink-0">
            <Link to="/stories">All stories <ArrowRight className="w-3 h-3" /></Link>
          </Button>
        </div>
        <div className="grid lg:grid-cols-2 gap-6">
          <Link to={`/stories/${hero.slug}`} className="group relative rounded-2xl overflow-hidden block shadow hover:shadow-2xl transition-shadow h-[420px]" data-testid={`story-${hero.slug}`}>
            <img src={hero.cover} alt={hero.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
              <div className="flex flex-wrap gap-1 mb-2">
                {hero.moods.slice(0, 3).map(m => {
                  const mood = TRAVEL_MOODS.find(x => x.id === m);
                  return mood ? <span key={m} className="text-[10px] px-2 py-0.5 rounded-full bg-white/15 backdrop-blur border border-white/20">{mood.emoji} {mood.label}</span> : null;
                })}
              </div>
              <h3 className="font-heading text-3xl font-bold leading-tight">{hero.title}</h3>
              <p className="text-white/80 mt-2 line-clamp-2">{hero.excerpt}</p>
              <div className="flex items-center gap-3 mt-3 text-xs text-white/70">
                <span>{hero.author}</span><span>·</span><span>{hero.readTime}</span>
              </div>
            </div>
          </Link>
          <div className="grid gap-4">
            {rest.slice(0, 3).map(s => (
              <Link key={s.slug} to={`/stories/${s.slug}`} className="group flex gap-4 bg-white rounded-xl overflow-hidden shadow hover:shadow-lg transition-shadow" data-testid={`story-${s.slug}`}>
                <div className="relative w-32 sm:w-40 shrink-0 overflow-hidden">
                  <img src={s.cover} alt={s.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                </div>
                <div className="flex-1 min-w-0 py-3 pr-3">
                  <h4 className="font-heading font-bold text-gray-900 group-hover:text-[#00AA6C] transition-colors line-clamp-2">{s.title}</h4>
                  <p className="text-xs text-gray-600 mt-1 line-clamp-2">{s.excerpt}</p>
                  <div className="text-[10px] text-gray-400 mt-2">{s.readTime}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
